import { countryAddressList } from "utils/countryAddressList";

/**
 * The billing details every Stripe payment method and payment carries: Stripe
 * uses them for ZIP checks and fraud scoring, and the server copies them to
 * the Stripe customer. Each value comes from what the customer typed, then
 * from their account; empty values are left out rather than sent blank.
 */

export type StripeBillingAddress = {
  line1?: string;
  line2?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  country?: string;
};

export type StripeBillingDetails = {
  name?: string;
  email?: string;
  phone?: string;
  address: StripeBillingAddress;
};

type BillingValues = {
  choldername?: string;
  x_address?: string;
  address2?: string;
  x_city?: string;
  x_state?: string;
  x_zip?: string;
  x_country?: string;
};

type AccountContact = {
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
  phone?: string | number | null;
  address1?: string | null;
  address2?: string | null;
  city?: string | null;
  state?: string | null;
  postal_code?: string | number | null;
  country?: string | null;
} | null;

type StateOption = { name?: string; value?: string; state_code?: string };

export type BillingDetailsInput = {
  values?: BillingValues | null;
  /** The form shows the street address: cc_billing_address is Y and the
   * page collects an address. The ZIP is sent either way. */
  showAddress: boolean;
  email?: string | null;
  phone?: string | number | null;
  account?: AccountContact;
  /** The state list for the chosen country, to send the state's code. */
  states?: StateOption[];
};

const POSTAL_FIELDS = ["ZIP", "Postal code", "CEDEX", "Eircode", "PIN"];
const STATE_FIELDS = [
  "State",
  "County",
  "Department",
  "District",
  "Emirate",
  "Island",
  "Oblast",
  "Parish",
  "Prefecture",
  "Province",
];
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const STATE_CODE_PATTERN = /^[A-Z0-9]{1,3}$/;

const clean = (value: unknown): string | undefined => {
  const text = value === null || value === undefined ? "" : String(value).trim();
  return text || undefined;
};

const fullName = (account?: AccountContact): string | undefined =>
  clean([account?.first_name, account?.last_name].map(clean).filter(Boolean).join(" "));

// Stripe prefers a state code ("CA"); the form holds the name ("California").
// Only US codes in the state list are real: elsewhere state_code is the first
// two letters of the name (India's "MA" is Madhya Pradesh, Maharashtra and
// Manipur), so outside the US the name is sent as typed, which Stripe accepts.
const stateCode = (
  state: string | undefined,
  country: string,
  states: StateOption[] = []
) => {
  if (!state) return undefined;
  if (country !== "US") return state;
  const upper = state.toUpperCase();
  const match = states.find(
    (option) =>
      option?.name === state ||
      option?.value === state ||
      option?.state_code?.toUpperCase() === upper
  );
  const code = clean(match?.state_code)?.toUpperCase();
  return code && STATE_CODE_PATTERN.test(code) ? code : state;
};

const withoutEmpty = <T extends Record<string, string | undefined>>(object: T) =>
  Object.fromEntries(
    Object.entries(object).filter(([, value]) => value !== undefined)
  ) as Partial<T>;

/**
 * For the bank and wallet forms, which take no address: the account's own
 * name and address. Their Payment Element hides the name and email fields, so
 * Stripe needs both; `nameFallback` covers a guest without an account name.
 */
export const buildAccountBillingDetails = ({
  account,
  email,
  phone,
  nameFallback,
}: {
  account?: AccountContact;
  email?: string | null;
  phone?: string | number | null;
  nameFallback?: string | null;
}): StripeBillingDetails => ({
  ...withoutEmpty({
    name: fullName(account) || clean(nameFallback),
    email: clean(email),
    phone: clean(phone) || clean(account?.phone),
  }),
  address: withoutEmpty({
    line1: clean(account?.address1),
    line2: clean(account?.address2),
    city: clean(account?.city),
    state: clean(account?.state),
    postal_code: clean(account?.postal_code),
    country: clean(account?.country),
  }),
});

type WalletAddress = {
  line1?: string | null;
  line2?: string | null;
  city?: string | null;
  state?: string | null;
  postal_code?: string | null;
  country?: string | null;
} | null;

/**
 * For Apple Pay and Google Pay: what the wallet sheet returned (payer name,
 * email, phone and the card's billing address), then the account.
 */
export const buildWalletBillingDetails = ({
  payerName,
  payerEmail,
  payerPhone,
  address,
  email,
  account,
}: {
  payerName?: string | null;
  payerEmail?: string | null;
  payerPhone?: string | null;
  address?: WalletAddress;
  email?: string | null;
  account?: AccountContact;
}): StripeBillingDetails => ({
  ...withoutEmpty({
    name: clean(payerName) || fullName(account),
    email: clean(payerEmail) || clean(email) || clean(account?.email),
    phone: clean(payerPhone) || clean(account?.phone),
  }),
  address: withoutEmpty({
    line1: clean(address?.line1),
    line2: clean(address?.line2),
    city: clean(address?.city),
    state: clean(address?.state),
    postal_code: clean(address?.postal_code),
    country: clean(address?.country)?.toUpperCase(),
  }),
});

export const buildStripeBillingDetails = ({
  values,
  showAddress,
  email,
  phone,
  account,
  states,
}: BillingDetailsInput): StripeBillingDetails => {
  const country = clean(values?.x_country)?.toUpperCase() || "US";
  const fields = Object.keys(countryAddressList()[country] || {});
  const has = (names: string[]) => names.some((name) => fields.includes(name));

  const address: StripeBillingAddress = { country };
  if (has(POSTAL_FIELDS)) address.postal_code = clean(values?.x_zip);
  if (showAddress) {
    if (fields.includes("Address line 1")) address.line1 = clean(values?.x_address);
    if (fields.includes("Address line 2")) address.line2 = clean(values?.address2);
    if (has(["City", "Area"])) address.city = clean(values?.x_city);
    if (has(STATE_FIELDS)) {
      address.state = stateCode(clean(values?.x_state), country, states);
    }
  }

  const typedEmail = clean(email);
  const accountEmail = clean(account?.email);
  const chosenEmail = [typedEmail, accountEmail].find(
    (candidate) => candidate && EMAIL_PATTERN.test(candidate)
  );

  return {
    ...withoutEmpty({
      name: clean(values?.choldername) || fullName(account),
      email: chosenEmail,
      phone: clean(phone) || clean(account?.phone),
    }),
    address: withoutEmpty(address),
  };
};
